import { Form } from "@formio/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import jwtDecode from "jwt-decode";
import useFormRoles from "./hooks/useFormRoles";
//import useFormPersistence from "./hooks/useFormPersistence";
import useFormSubmit from "./hooks/useFormSubmit";

import WizardStepper from "./WizardStepper";
import { forEachInputComponent, applyAutoDate, getCurrentLocation } from "./utils/formUtils";
import { selectUserActions } from "../../store/slices/database/userAccessDataSlice";
import useAuth from "hooks/useAuth";
import * as Constants from "common/constants";

const Formioform = ({ form, withWizard = true, onSubmit }) => {
  const formRef = useRef();
  const navigate = useNavigate();

  const { t, i18n } = useTranslation();
  const userActions = useSelector(selectUserActions);

  const roles = useFormRoles(userActions);
 // const { save, load, clear } = useFormPersistence(form);
  const keycloakAuth = useAuth();
  const { validate } = useFormSubmit({ formRef, t });

  const [currentPage, setCurrentPage] = useState(0);
  const [formReady, setFormReady] = useState(false);

 // ✅ Memoized form (immutable)
  const finalForm = useMemo(() => {
    const token = keycloakAuth.token
    const baseURL = Constants.base_url;
    if (!form) return null;
    const userId = jwtDecode(token).sub;

    const tempResources = Object.fromEntries(
      Object.entries(i18n.store.data).map(([lang, value]) => [
        lang,
        value.translation
      ])
    );
    console.log(tempResources);

    return {
      ...form,
      token,
      userId,
      baseURL,
      settings: {
        ...form.settings,
        roles,
        language: i18n.resolvedLanguage,
        translatedData : tempResources
      },
    };
  }, [form, roles, i18n]);

const totalPages = finalForm?.components?.length || 0;
  const isFirstPage = currentPage === 0;
  const isLastPage = currentPage >= totalPages - 1;

  // ✅ Load saved data when form is ready
  useEffect(() => {
    const init = async () => {
      // const saved = await load();
      //if (!saved || !formRef.current) return;

      formRef.current.submission = { data: form.settings.data };
      formRef.current.redraw();
    };

    if (formReady) init();
  }, [formReady]);

  // ✅ Save on change
  const handleChange = (e) => {
    if (!e.changed) return;
   // save(currentPage, e.data);
  };

  // ✅ Submit
  const handleSubmit = async () => {
    if (!validate()) return;
  //  await clear();

    const submission = formRef.current.submission;

    // apply auto fields
    forEachInputComponent(finalForm.components, async (comp) => {
      if (comp.type === "datetime") {
        applyAutoDate(comp, submission.data);
      }
      if (comp.type === "userGeolocation") {
        const pos = await getCurrentLocation();
        submission.data[comp.key] = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      }
    });

    onSubmit?.(submission);
  };
  const handleNext = async () => {
    try {
      if (!formRef.current) return;

      await formRef.current.nextPage();

      setCurrentPage(formRef.current.page);
    } catch (err) {
      console.error(err);
    }
  };

  const handleBack = async () => {
    try {
      if (!formRef.current) return;

      await formRef.current.prevPage();

      setCurrentPage(formRef.current.page);
    } catch (err) {
      console.error(err);
    }
  };
const tempResources = useMemo(() => {
  return Object.fromEntries(
    Object.entries(i18n.store.data).map(([lang, value]) => [
      lang,
      value.translation,
    ])
  );
}, [i18n.store.data]);

  return (
    <>
      {/* Stepper Wizard */}
      {withWizard && finalForm && (
        <WizardStepper
          form={finalForm}
          formioFormRef={formRef}
          setCurrentPage={setCurrentPage}
          currentPage={currentPage}
          formReady={formReady}
           options={{
            language: i18n.language,
            i18n: tempResources
          }} 
        />
      )}

      {/* FormIO Form */}
      {finalForm && (
        <Form
          form={finalForm}
          formReady={(instance) => {
            formRef.current = instance;
            setFormReady(true);
          }}
          onChange={handleChange}
          onSubmit={handleSubmit}
          options={{
            language: i18n.language,
            i18n: tempResources, 
            noAlerts: true, 
            buttonSettings: { showSubmit: false }
          }} // hide default FormIO buttons
        />
      )}

      {/* Manual Back/Next buttons (optional, can remove if WizardStepper handles it) */}
      <div
        style={{
          marginTop: "24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {!isFirstPage ? (
          <button
            onClick={handleBack}
            style={{
              padding: "10px 20px",
              borderRadius: "10px",
              border: "1px solid #d1d5db",
              background: "#fff",
              color: "#374151",
              cursor: "pointer",
              fontWeight: 500,
            }}
          >
            ← {t("Back")}
          </button>
        ) : (
          <div />
        )}

        {!isLastPage ? (
          <button
            onClick={handleNext}
            style={{
              padding: "10px 24px",
              borderRadius: "10px",
              border: "none",
              background: "#2563eb",
              color: "#fff",
              cursor: "pointer",
              fontWeight: 600,
              boxShadow: "0 4px 12px rgba(37,99,235,0.25)",
            }}
          >
            {t("Next")} →
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            style={{
              padding: "10px 24px",
              borderRadius: "10px",
              border: "none",
              background: "#16a34a",
              color: "#fff",
              cursor: "pointer",
              fontWeight: 600,
              boxShadow: "0 4px 12px rgba(22,163,74,0.25)",
            }}
          >
            ✓ {t("Submit")}
          </button>
        )}
</div>
    </>
  );
};

export default Formioform;